import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, IconButton, Tooltip } from '@material-ui/core';
import { CloudUpload as CloudUploadIcon } from "components/MaterialSymbol/MaterialIcons";
import { Close as CloseIcon } from "components/MaterialSymbol/MaterialIcons";

const useStyles = makeStyles((theme) => ({
  dropzone: {
    border: '2px dashed #e0e0e0',
    borderRadius: '12px',
    padding: (props) => props.compact ? '8px' : '40px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: '#fafafa',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: '#f0f4f8',
      '& $overlay': {
        opacity: 1
      }
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    minHeight: (props) => props.compact ? '80px' : '160px',
    width: '100%',
    boxSizing: 'border-box'
  },
  active: {
    borderColor: theme.palette.primary.main + ' !important',
    backgroundColor: '#e3f2fd !important',
    boxShadow: `0 0 0 2px ${theme.palette.primary.main} inset`,
  },
  previewImg: {
    maxWidth: '100%',
    maxHeight: (props) => props.compact ? '120px' : '300px',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
    borderRadius: '8px',
    flexShrink: 1
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    borderRadius: '12px',
    gap: '4px',
    pointerEvents: 'none' // Ensure overlay doesn't block drop
  },
  removeAction: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    zIndex: 10,
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    }
  }
}));

export default function HomepageCategoryFileUpload({ onFileSelect, initialPreview, compact = false }) {
  const classes = useStyles({ compact });
  const [preview, setPreview] = useState(initialPreview || null);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    // Debug helper
    if (fileRejections.length > 0) {
      console.warn('File rejected:', fileRejections);
    }

    const selectedFile = acceptedFiles[0];
    if (selectedFile) {
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreview(objectUrl);
      if (onFileSelect) onFileSelect(selectedFile);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/svg+xml': ['.svg'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp']
    },
    multiple: false,
    noClick: false,
    noKeyboard: false
  });

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview(null);
    if (onFileSelect) onFileSelect(null);
  };

  React.useEffect(() => {
    setPreview(initialPreview);
  }, [initialPreview]);

  // Handle global drag events to prevent browser opening the file
  const handleContainerDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box position="relative" onDragOver={handleContainerDragOver}>
      <div 
        {...getRootProps()} 
        className={`${classes.dropzone} ${isDragActive ? classes.active : ''}`}
      >
        <input {...getInputProps()} />
        
        {preview ? (
          <>
            <img src={preview} alt="Category image" className={classes.previewImg} />
            <div className={classes.overlay}>
              <CloudUploadIcon fontSize={compact ? "small" : "medium"} />
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                {compact ? 'Replace' : 'Click or Drop to Replace'}
              </Typography>
            </div>
          </>
        ) : (
          <>
            <CloudUploadIcon style={{ fontSize: compact ? '24px' : '48px', color: '#bdbdbd' }} />
            <Typography 
                variant={compact ? "caption" : "h6"} 
                style={{ fontWeight: 500, color: '#455a64', lineHeight: 1.2 }}
            >
              {compact ? 'Drop or click' : 'Click or drag image to upload'}
            </Typography>
            {!compact && (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: '4px' }}>
                SVG, PNG, WebP or JPG (max. 2MB)
              </Typography>
            )}
          </>
        )}
      </div>

      {preview && (
        <Tooltip title="Remove Image">
          <IconButton 
            size="small" 
            className={classes.removeAction} 
            onClick={handleRemove}
          >
            <CloseIcon style={{ fontSize: compact ? '14px' : '18px' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
